import React from 'react';
import {
  Person,
  PersonPermission,
  useClientAppInvitationService
} from 'nekst-api';
import { ConfirmPositiveActionPopup } from 'features/nekst-widgets';
import {
  Button,
  ButtonColor,
  ButtonSize,
  usePopupManager
} from 'ui-builder';
import { useAuthorizationChecker } from 'authorization-scope';
import { ClientAppToggleWrapper } from 'feature-toggle';

function InviteToClientAppPopup(
  props: {
    person: Person,
  },
) {
  const email = props.person.profile.primaryEmail;
  const message = `By clicking "Confirm", an invitation email will be sent to ${props.person.profile.name.fullName} at ${email}.`;

  const service = useClientAppInvitationService();

  return (
    <ConfirmPositiveActionPopup
      title="Invite to Client App"
      message={message}
      submitFormFunc={async () => service.sendInvitation(props.person.id)}
    />
  );
}

function useInviteToClientAppLogic(person: Person) {
  const popupManager = usePopupManager();
  const { isGranted } = useAuthorizationChecker();

  const hasEmail = !!person.profile.primaryEmail;
  const hasPermission = isGranted(PersonPermission.INVITE_TO_CLIENT_APP);
  const hasPendingInvitation = !!person.portalInvitationSentAt && !person.portalInvitationAcceptedAt;

  const isVisible = hasEmail && hasPermission && hasPendingInvitation;

  const handleAction = () => {
    popupManager.openPopup(
      <InviteToClientAppPopup person={person} />
    );
  };

  return {
    handleAction,
    isVisible,
  };
}

export function InviteToClientAppButton(
  props: {
    person: Person,
  }
) {
  const buttonLogic = useInviteToClientAppLogic(props.person);

  if (!buttonLogic.isVisible) {
    return null;
  }

  return (
    <ClientAppToggleWrapper>
      <Button
        color={ButtonColor.BLUE}
        isFilled
        size={ButtonSize.MEDIUM}
        text="Invite to Client App"
        onClick={buttonLogic.handleAction}
      />
    </ClientAppToggleWrapper>
  );
}
